import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { CompassParagraph } from "../../../components/common/styledComponents";
import {
  SBTI_LONG_TERM_COLUMNS,
  SBTI_MESSAGE,
  SBTI_NEAR_TERM_COLUMNS,
  SBTI_NET_ZERO_COLUMNS,
  SBTIColumn,
  SBTIMetric,
} from "./constants";
import {
  ElementContainer,
  FlexibleContainer,
  RowContainer,
} from "../../../components/containers/styledComponents";
import {
  Icon,
  IconContainer,
} from "../../../components/visuals/styledComponents";
import warningIcon from "../../../assets/svg/warning_circle.svg";
import { FlexibleTable } from "../../../components/tables/FlexibleTable";
import { ToggleAccordionBlueText } from "../../../components/buttons/ToggleAccordionBlueText";
import {
  PublicMetric,
  TargetStatus,
} from "../../../models/CompanyprofileModels";
import {
  getSbtiTargetStatus,
  buildSbtiDataEntries,
  getMetricValue,
} from "./util";
import { DataEntry } from "../../../components/tables/FlexibleTable";
import { CompassTag } from "../../../components/visuals/CompassTag";
import { greyText } from "../../../styles";

const TableContainer = styled.div`
  display: flex;
  width: 450px;
  min-width: 300px;
`;
export const TabSBTI = ({
  metricsData,
}: {
  metricsData: PublicMetric[];
}): JSX.Element => {
  const [isCardExpanded, setIsCardExpanded] = useState(false);
  const handleCardExpand = (isActive: boolean): void => {
    setIsCardExpanded(isActive);
  };
  const [dataEntries, setDataEntries] = useState<DataEntry[]>([]);
  const [description, setDescription] = useState<string>("");

  const getSbtiDataEntries = (): DataEntry[] => {
    const baseSbtiDataEntries = buildSbtiDataEntries(metricsData);

    const nearTermTargetStatus = getSbtiTargetStatus(
      getMetricValue(
        SBTIMetric.Near_term_Target_Status,
        metricsData
      ) as TargetStatus
    );

    const longTermTargetStatus = getSbtiTargetStatus(
      getMetricValue(
        SBTIMetric.Long_term_Target_Status,
        metricsData
      ) as TargetStatus
    );

    const netZeroTargetStatus = getSbtiTargetStatus(
      getMetricValue(SBTIMetric.Net_Zero_Committed, metricsData) as TargetStatus
    );

    const nearTerm = {
      ...baseSbtiDataEntries[0],
      [SBTIColumn.NEAR_TERM_TARGET_STATUS]: (
        <CompassTag {...nearTermTargetStatus} />
      ),
    };

    const longTerm = {
      ...baseSbtiDataEntries[1],
      [SBTIColumn.LONG_TERM_TARGET_STATUS]: (
        <CompassTag {...longTermTargetStatus} />
      ),
    };

    const netZero = {
      ...baseSbtiDataEntries[2],
      [SBTIColumn.NET_ZERO_STATUS]: <CompassTag {...netZeroTargetStatus} />,
    };

    return [nearTerm, longTerm, netZero];
  };

  useEffect(() => {
    setDataEntries(getSbtiDataEntries());
    setDescription(
      getMetricValue(SBTIMetric.Full_Target_Language, metricsData) as string
    );
  }, [metricsData]);

  return (
    <FlexibleContainer
      fd="column"
      w={"100%"}
      p={"10px 5px"}
      ofy="auto"
      fw="wrap"
    >
      <RowContainer jc="flex-start" ai="center" mt={1} mb={1}>
        <IconContainer size={25} rotate={180}>
          <Icon src={warningIcon} />
        </IconContainer>
        <CompassParagraph fontSize={12} ml={-4} color={greyText}>
          {SBTI_MESSAGE}
        </CompassParagraph>
      </RowContainer>
      <RowContainer ai="center" jc="flex-start" wrap="wrap" mt={1}>
        <TableContainer>
          <FlexibleTable
            columns={SBTI_NEAR_TERM_COLUMNS}
            data={dataEntries.slice(0, 1)}
          />
        </TableContainer>
        <TableContainer>
          <FlexibleTable
            columns={SBTI_LONG_TERM_COLUMNS}
            data={dataEntries.slice(1, 2)}
          />
        </TableContainer>
        <TableContainer>
          <FlexibleTable
            columns={SBTI_NET_ZERO_COLUMNS}
            data={dataEntries.slice(2)}
          />
        </TableContainer>
      </RowContainer>
      <RowContainer>
        <ElementContainer flexDirection="column">
          <ToggleAccordionBlueText
            textActive="View Target Description"
            textInactive="Hide Target Description"
            handleChange={handleCardExpand}
          />
          {isCardExpanded && (
            <FlexibleContainer mt="12px">
              <CompassParagraph color={greyText}>
                {description}
              </CompassParagraph>
            </FlexibleContainer>
          )}
        </ElementContainer>
      </RowContainer>
    </FlexibleContainer>
  );
};
